import React, { useEffect, useState } from 'react';
import { usePeriod } from '../contexts/PeriodContext';
import { useAuth } from '../contexts/AuthContext';
import API from '../lib/api';
import { StatusChip } from '../components/app/StatusChip';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend
} from 'recharts';
import {
  TrendingUp, AlertTriangle, CheckCircle2, Clock,
  DollarSign, ReceiptText, RefreshCw
} from 'lucide-react';

const CHART_COLORS = ['#3b82f6','#10b981','#f59e0b','#8b5cf6','#ef4444','#06b6d4','#f97316'];

function KpiCard({ label, value, sub, icon: Icon, color = 'blue' }) {
  const colors = {
    blue: 'text-blue-600 bg-blue-50',
    green: 'text-emerald-600 bg-emerald-50',
    orange: 'text-orange-600 bg-orange-50',
    amber: 'text-amber-600 bg-amber-50',
  };
  return (
    <div className="kpi-card flex items-start gap-4">
      <div className={`p-2.5 rounded-lg ${colors[color]}`}>
        <Icon size={18} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-xs text-slate-500 font-medium uppercase tracking-wide">{label}</div>
        <div className="text-2xl font-bold text-slate-900 tabular-nums mt-0.5">{value}</div>
        {sub && <div className="text-xs text-slate-500 mt-0.5">{sub}</div>}
      </div>
    </div>
  );
}

const formatCurrency = (n) => {
  if (!n && n !== 0) return '—';
  return new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n);
};

export default function DashboardPage() {
  const { activePeriod } = usePeriod();
  const { user } = useAuth();
  const [data, setData]   = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = activePeriod ? { period_id: activePeriod.id } : {};
      const res = await API.get('/analytics/dashboard', { params });
      setData(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [activePeriod?.id]);

  if (loading) return (
    <div className="p-8 flex items-center justify-center">
      <RefreshCw size={24} className="animate-spin text-slate-400" />
    </div>
  );

  const kpis = data?.kpis || {};

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Dashboard</h1>
          <p className="text-sm text-slate-500">
            {activePeriod ? `Period: ${activePeriod.label}` : 'All periods'}
          </p>
        </div>
        <button
          onClick={load}
          className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 px-2 py-1 rounded hover:bg-slate-100 transition-colors"
        >
          <RefreshCw size={13} /> Refresh
        </button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div data-testid="dashboard-kpi-total-spend">
          <KpiCard label="Total Spend" value={formatCurrency(kpis.total_spend)} icon={DollarSign} color="blue"
            sub={`${kpis.total_transactions || 0} transactions`} />
        </div>
        <div data-testid="dashboard-kpi-coded-percent">
          <KpiCard label="Coded" value={`${kpis.coded_pct || 0}%`} icon={CheckCircle2} color="green"
            sub={`${kpis.coded_count || 0} of ${kpis.total_transactions || 0}`} />
        </div>
        <div data-testid="dashboard-kpi-exceptions-count">
          <KpiCard label="Exceptions" value={kpis.exceptions_count || 0} icon={AlertTriangle} color="orange"
            sub="Needs attention" />
        </div>
        <div data-testid="dashboard-kpi-pending-approvals">
          <KpiCard label="Pending Approval" value={kpis.submitted_count || 0} icon={Clock} color="amber"
            sub={`${kpis.approved_count || 0} approved`} />
        </div>
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Top Merchants */}
        <div className="bg-white rounded-lg border shadow-sm p-5">
          <h3 className="text-sm font-semibold text-slate-800 mb-4">Top Merchants by Spend</h3>
          {(data?.top_merchants || []).length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart
                data={(data?.top_merchants || []).slice(0, 10).map(m => ({ ...m, merchant: m.merchant.slice(0, 20) }))}
                layout="vertical"
                margin={{ left: 0, right: 30, top: 0, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f1f5f9" />
                <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
                <YAxis type="category" dataKey="merchant" tick={{ fontSize: 10 }} width={100} />
                <Tooltip formatter={(v) => [formatCurrency(v), 'Amount']} />
                <Bar dataKey="amount" fill="#3b82f6" radius={[0, 3, 3, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-52 flex items-center justify-center text-sm text-slate-400">No data</div>
          )}
        </div>

        {/* Spend by Category */}
        <div className="bg-white rounded-lg border shadow-sm p-5">
          <h3 className="text-sm font-semibold text-slate-800 mb-4">Spend by Category</h3>
          {(data?.spend_by_category || []).filter(c => c.amount > 0).length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie
                  data={(data?.spend_by_category || []).filter(c => c.amount > 0).slice(0, 8)}
                  dataKey="amount" nameKey="category"
                  cx="50%" cy="50%"
                  outerRadius={90}
                  label={({ category, percent }) => `${category.slice(0,12)} ${(percent*100).toFixed(0)}%`}
                  labelLine={false}
                  fontSize={10}
                >
                  {(data?.spend_by_category || []).slice(0, 8).map((_, i) => (
                    <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(v) => [formatCurrency(v), 'Amount']} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-52 flex items-center justify-center text-sm text-slate-400">No coded transactions yet</div>
          )}
        </div>
      </div>

      {/* Period Status + Status Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Periods */}
        <div className="bg-white rounded-lg border shadow-sm p-5">
          <h3 className="text-sm font-semibold text-slate-800 mb-4">Statement Periods</h3>
          <div className="space-y-2">
            {(data?.periods || []).length === 0 ? (
              <p className="text-sm text-slate-400">No periods created yet</p>
            ) : (
              (data?.periods || []).map(p => (
                <div key={p.id} className="flex items-center justify-between py-1.5 border-b last:border-0">
                  <div>
                    <span className="text-sm font-medium">{p.label}</span>
                    <span className="text-xs text-slate-400 ml-2">{p.period_start} → {p.period_end}</span>
                  </div>
                  <StatusChip status={p.status} size="xs" />
                </div>
              ))
            )}
          </div>
        </div>

        {/* Status Distribution */}
        <div className="bg-white rounded-lg border shadow-sm p-5">
          <h3 className="text-sm font-semibold text-slate-800 mb-4">Transaction Status Distribution</h3>
          {(data?.status_distribution || []).length === 0 ? (
            <div className="h-40 flex items-center justify-center text-sm text-slate-400">No transactions yet</div>
          ) : (
            <div className="space-y-2">
              {(data?.status_distribution || []).map(({ status, count }) => {
                const pct = kpis.total_transactions > 0 ? Math.round(count / kpis.total_transactions * 100) : 0;
                return (
                  <div key={status} className="flex items-center gap-3">
                    <StatusChip status={status} size="xs" />
                    <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full bg-blue-400 rounded-full" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="text-xs tabular-nums text-slate-600 w-10 text-right">{count}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
